import { get, has, merge } from 'lodash';
import {
  CHART_COLORS,
  VEGA_SPEC_AXIS_SETTINGS,
  VEGA_SPEC_LEGEND_SETTINGS,
} from 'constants/chart-spec';
import { CHART_CATEGORIES } from 'constants/chart-categories';
import { generateVegaLabelTextExpression } from 'utils/chart/vega-expressions';
import { generateVegaTooltip } from 'utils/chart/tooltips';
import {
  generateVegaSortParams,
  generateVegaTypeCoercionTransforms,
  generateVegaSortingAndLimitingTransforms,
} from 'utils/chart/transforms';

export default {
  name: '100% Stacked Column',
  displayName: '100%',
  order: 2,
  flavour: 'discrete',
  category: CHART_CATEGORIES.COLUMN,
  iconValue: 'column-stacked-100',
  channels: [
    {
      name: 'x',
      displayName: 'X Axis',
      required: false,
      channelType: 'category',
      intent: 'category-axis',
    },
    {
      name: 'y',
      displayName: 'Y Axis',
      required: true,
      channelType: 'aggregation',
      intent: 'value-axis',
      multipleSeries: true,
    },
    {
      name: 'color',
      displayName: 'Series',
      required: false,
      channelType: 'category',
      intent: 'series',
    },
  ],
  specFn: (encodings, customisations) => {
    const sortParams = generateVegaSortParams(encodings);
    return {
      autosize: {
        type: 'fit',
        contains: 'padding',
      },
      style: 'cell',
      data: [
        {
          name: 'transformedData',
          source: 'values',
          transform: [
            ...generateVegaTypeCoercionTransforms('y'),
            ...generateVegaSortingAndLimitingTransforms(encodings),
            {
              type: 'stack',
              groupby: ['x'],
              field: 'y',
              sort: {
                field: [sortParams.color.field, 'tooltip'],
                order: ['descending', 'descending'],
              },
              as: ['y_start', 'y_end'],
              offset: 'normalize',
            },
          ],
        },
      ],
      marks: [
        {
          name: 'marks',
          type: 'rect',
          style: ['bar'],
          from: { data: 'transformedData' },
          encode: {
            update: {
              x: { scale: 'x', field: 'x' },
              y: { scale: 'y', field: 'y_end' },
              y2: { scale: 'y', field: 'y_start' },
              fill: { scale: 'color', field: 'color' },
              width: { scale: 'x', band: true },
              tooltip: {
                signal: generateVegaTooltip(encodings, customisations),
              },
            },
          },
        },
      ],
      scales: [
        {
          name: 'x',
          type: 'band',
          domain: { data: 'transformedData', field: 'x', sort: sortParams.x },
          range: [0, { signal: 'width' }],
          paddingInner: 0.1,
          paddingOuter: 0.05,
        },
        {
          name: 'y',
          type: 'linear',
          domain: { data: 'transformedData', fields: ['y_start', 'y_end'] },
          zero: true,
          range: [{ signal: 'height' }, 0],
        },
        {
          name: 'color',
          type: 'ordinal',
          domain: {
            data: 'transformedData',
            field: 'color',
            sort: sortParams.color,
          },
          range:
            get(customisations.options, 'colorDiscrete.value') || CHART_COLORS,
        },
      ],
      axes: [
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'x',
          orient: 'bottom',
          grid: false,
          title: get(encodings, 'x.axis.title', ''),
          encode: {
            labels: {
              update: {
                angle: { value: 270 },
                align: { value: 'right' },
                baseline: { value: 'middle' },
                text: {
                  signal: generateVegaLabelTextExpression(
                    encodings.x,
                    customisations.channels.x
                  ),
                },
              },
            },
          },
        }),
        merge({}, VEGA_SPEC_AXIS_SETTINGS, {
          scale: 'y',
          orient: 'left',
          grid: true,
          tickCount: { signal: 'ceil(height/40)' },
          title: get(encodings, 'y.axis.title', ''),
          format: '.0%',
        }),
      ],
      legends: !has(encodings, 'color.legend.title')
        ? undefined
        : [
            merge({}, VEGA_SPEC_LEGEND_SETTINGS, {
              title: get(encodings, 'color.legend.title'),
              fill: 'color',
              encode: {
                labels: {
                  update: {
                    text: {
                      signal: generateVegaLabelTextExpression(
                        encodings.color,
                        customisations.channels.color
                      ),
                    },
                  },
                },
              },
            }),
          ],
    };
  },
};
